import React, { useState, useEffect, useCallback } from 'react';
import { RiCollapseDiagonalLine } from "react-icons/ri";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaFilter } from "react-icons/fa";
import { TbReload, TbArrowsSort } from "react-icons/tb";

const API_URL = process.env.REACT_APP_API_BASE || '';

function StatusBadge({ status }) {
    const s = (status || '').toLowerCase();
    const map = {
        running: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        completed: 'bg-green-100 text-green-700 border-green-200',
        failed: 'bg-red-100 text-red-600 border-red-200',
        pending: 'bg-gray-100 text-gray-500 border-gray-200',
        scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
    };
    return (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border capitalize ${map[s] || 'bg-gray-100 text-gray-500 border-gray-200'}`}>
            {status || 'pending'}
        </span>
    );
}

function ScheduleReport() {
    const [showfilter, setshowfilter] = useState(false);
    const [Campaignname, setCampaignname] = useState('');
    const [TemplateName, setTemplateName] = useState('');
    const [TemplateType, setTemplateType] = useState('');
    const [Interface, setInterface] = useState('');

    const [allData, setAllData] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Pagination state
    const [currentPage, setcurrentPage] = useState(1);
    const [usersperpage, setUsersperpage] = useState(10);
    const perPageOptions = [10, 20, 50, 100];

    const indexoflastuser = currentPage * usersperpage;
    const indexoffirstuser = indexoflastuser - usersperpage;
    const filteruser = filtered.slice(indexoffirstuser, indexoflastuser);
    const totalpages = Math.ceil(filtered.length / usersperpage);

    const [modalData, setModalData] = useState([]);
const [modalLoading, setModalLoading] = useState(false);
const [modalCampaign, setModalCampaign] = useState(null); // holds the campaign row

    // -- Fetch: all campaigns — ScheduleReport shows ALL campaigns (running/scheduled/completed)


    const openCampaignReport = async (row) => {
        setModalCampaign(row);
        setModalLoading(true);
        setModalData([]);
        try {
            const r = await fetch(`${API_URL}/auth/campaign/${row.id}`, { credentials: 'include' });
            const data = await r.json();
            setModalData(data.data || []);
        } catch {
            setModalData([]);
        } finally {
            setModalLoading(false);
        }
    };
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}/auth/campaign/report`, { credentials: 'include' });
            const json = await res.json();
            if (!json.success) throw new Error('Failed');
            setAllData(json.data || []);
            setFiltered(json.data || []);
        } catch (e) {
            setError('Failed to load schedule data.');
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const applyFilter = () => {
        let d = [...allData];
        if (Campaignname) d = d.filter(r => (r.campaign_name || '').toLowerCase().includes(Campaignname.toLowerCase()));
        if (TemplateName) d = d.filter(r => (r.template_name || '').toLowerCase().includes(TemplateName.toLowerCase()));
        if (TemplateType) d = d.filter(r => (r.template_type || '').toLowerCase() === TemplateType.toLowerCase());
        if (Interface) d = d.filter(r => (r.interface || '').toLowerCase() === Interface.toLowerCase());
        setFiltered(d);
        setcurrentPage(1);
    };

    const clearFilter = () => {
        setCampaignname(''); setTemplateName(''); setTemplateType(''); setInterface('');
        setFiltered(allData);
        setcurrentPage(1);
    };

    // When usersperpage changes, reset to page 1 if necessary
    useEffect(() => {
        if (currentPage > Math.ceil(filtered.length / usersperpage)) {
            setcurrentPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [usersperpage, filtered.length]);

    const interfaceOptions = ['None', 'Web', 'Http', 'OTP', 'Excel', 'Scheduler', 'ExcelPlugin', 'SMPP', 'XML', 'OneToMany', 'Android', 'IPhone', 'Windows', 'Categorize', 'File', 'ChatAgent'];

    const columns = ['Scheduled Date', 'Campaign Date', 'Campaign Info', 'Template Info', 'Interface / Channel', 'Cost', 'Total Count', 'Status', 'Action'];

    return (
        <>
            {/* Info Banner */}
            <div className='w-full flex justify-end items-center py-2'>
                <div className='w-full sm:w-[55%] bg-[#ecf2fa] border-[#2e66a8] border rounded-lg p-2 text-[12px] text-[#2e66a8]'>
                    ℹ️ Campaigns cannot be rescheduled if less than 2 minutes remain before their scheduled execution.
                </div>
            </div>

            {/* Top Bar */}
            <div className='w-full flex items-center justify-between mb-1'>
                <p className='text-[12px] text-gray-500 dark:text-gray-400'>
                    Total: <span className='font-bold text-[#37715d]'>{allData.length}</span> campaigns
                </p>
                <button
                    onClick={fetchData}
                    className='flex items-center border border-[#37715d] rounded-lg p-2 text-[#37715d] gap-1 hover:bg-[#37715d] hover:text-white transition-colors'
                >
                    <TbReload className='text-[13px]' />
                    <span className='text-[12px] font-bold'>Refresh</span>
                </button>
            </div>

            {/* Filter Bar */}
            <div
                onClick={() => setshowfilter(!showfilter)}
                className='w-full flex justify-between px-4 py-2 bg-[#e0e0e04b] rounded-t-[20px] cursor-pointer'
            >
                <div className='flex items-center text-[#576071] font-semibold gap-3 dark:text-white'>
                    <RiCollapseDiagonalLine /> Filter Records
                </div>
                <div className='flex items-center text-[#576071] dark:text-white'>
                    {showfilter ? <IoIosArrowDown /> : <IoIosArrowUp />}
                </div>
            </div>

            {showfilter && (
                <div className='bg-[#edf1f5] py-2 px-4 w-full dark:bg-[#121519] z-20'>
                    <div className='w-full flex flex-wrap gap-2'>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-white'>Campaign Name</label>
                            <input
                                value={Campaignname}
                                onChange={e => setCampaignname(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                                type="text"
                            />
                        </div>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-white'>Template Name</label>
                            <input
                                value={TemplateName}
                                onChange={e => setTemplateName(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                                type="text"
                            />
                        </div>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-white'>Template Type</label>
                            <select
                                value={TemplateType}
                                onChange={e => setTemplateType(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                            >
                                <option value="">Select Template Type</option>
                                <option value="marketing">Marketing</option>
                                <option value="utility">Utility</option>
                                <option value="authentication">Authentication</option>
                            </select>
                        </div>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                            <label className='text-[#000] text-[12px] font-semibold dark:text-white'>Interface</label>
                            <select
                                value={Interface}
                                onChange={e => setInterface(e.target.value)}
                                className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                            >
                                <option value="">Select Interface</option>
                                {interfaceOptions.map((v, i) => (
                                    <option key={i} value={v.toLowerCase()}>{v}</option>
                                ))}
                            </select>
                        </div>

                        <div className='w-full sm:w-[48%] lg:w-[19%] flex items-center gap-1 mt-4'>
                            <button onClick={applyFilter} className='flex items-center justify-center border border-[#37715d] rounded-lg p-2 text-[#37715d] gap-2 hover:bg-[#37715d] hover:text-white w-full sm:w-auto transition-colors'>
                                <FaFilter /><span className='text-[12px] font-bold'>Filter</span>
                            </button>
                            <button onClick={clearFilter} className='flex items-center justify-center border border-[#3f7fca] rounded-lg p-2 text-[#3f7fca] gap-2 hover:bg-[#3f7fca] hover:text-white w-full sm:w-auto transition-colors'>
                                <TbReload /><span className='text-[12px] font-bold'>Clear Filter</span>
                            </button>
                        </div>

                    </div>
                </div>
            )}

            {error && (
                <div className='w-full bg-red-50 border border-red-200 text-red-600 text-[12px] px-4 py-2 rounded my-2'>
                    {error}
                </div>
            )}

            {/* Table */}
            <div className='overflow-x-auto'>
                <table className="w-full overflow-hidden border border-[#dee2e6]">
                    <thead>
                        <tr className="text-left">
                            {columns.map(col => (
                                <th key={col} className="p-1 cursor-pointer border border-[#dee2e6] bg-[#f6f9fc] dark:bg-[#1a1f24]">
                                    <div className="flex justify-between items-center gap-2">
                                        <span className='text-[11px] text-[#576071] dark:text-white font-semibold'>{col}</span>
                                        {col !== 'Action' && (
                                            <button type="button" className="text-gray-400 hover:text-black"><TbArrowsSort /></button>
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr className="bg-[#f6f9fc] dark:bg-[#121519]">
                                <td colSpan={columns.length} className="py-8 text-center">
                                    <div className="flex items-center justify-center gap-2 text-[12px] text-gray-400">
                                        <div className="w-4 h-4 border-2 border-[#37715d] border-t-transparent rounded-full animate-spin" />
                                        Loading...
                                    </div>
                                </td>
                            </tr>
                        ) : filteruser.length === 0 ? (
                            <tr className="text-center bg-[#f6f9fc] dark:bg-[#121519]">
                                <td colSpan={columns.length} className="py-8 text-[12px] text-gray-400">No data found</td>
                            </tr>
                        ) : (
                            filteruser.map((row, i) => (
                                <tr
                                    key={row.id || i}
                                    className="text-gray-500 odd:bg-white even:bg-[#edf1f5] dark:odd:bg-[#121519] dark:even:bg-[#1a1f24] hover:bg-[#e8f0fa] dark:hover:bg-[#2a2f35] transition-colors"
                                >
                                    {/* Scheduled Date — same as created_at since API doesn't have scheduled_at */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white text-gray-400">
                                        {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
                                    </td>
                                    {/* Campaign Date */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white text-gray-400">
                                        {row.created_at ? new Date(row.created_at).toLocaleDateString() : '-'}
                                    </td>
                                    {/* Campaign Info */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white font-medium">
                                        <div>{row.campaign_name || 'Untitled'}</div>
                                        <div className='text-[10px] text-gray-400'>ID: {row.id}</div>
                                    </td>
                                    {/* Template Info */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white">
                                        <div>{row.template_name || '-'}</div>
                                        <div className='text-[10px] text-gray-400 capitalize'>{row.template_type || '-'}</div>
                                    </td>
                                    {/* Interface / Channel */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white capitalize">
                                        {row.interface || 'web'} / WhatsApp
                                    </td>
                                    {/* Cost */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white">-</td>
                                    {/* Total Count */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white font-semibold text-center">
                                        {row.total_count ?? '-'}
                                    </td>
                                    {/* Status */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6]">
                                        <StatusBadge status={row.status} />
                                    </td>
                                    {/* Action */}
                                    <td className="p-1 text-[11px] border border-[#dee2e6]">
    <div className='flex gap-1'>
     
        <button
            onClick={() => openCampaignReport(row)}
            className='text-[#37715d] text-[10px] font-semibold border border-[#37715d] px-1.5 py-0.5 rounded hover:bg-[#37715d] hover:text-white transition-colors'
        >
            Report
        </button>
    </div>
</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className='flex flex-col md:flex-row md:justify-between w-full mt-2 md:items-center gap-2'>
                <div className='flex items-center gap-2 mt-1'>
                    <span className="text-[11px] text-[#576071]">Rows per page:</span>
                    <select
                        className="border border-[#dae2ea] rounded px-1 py-0.5 text-[11px] focus:outline-none"
                        value={usersperpage}
                        onChange={e => {
                            setUsersperpage(Number(e.target.value));
                            setcurrentPage(1);
                        }}
                    >
                        {perPageOptions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                </div>
                <p className='text-[11px] text-[#3f7fc8] mt-1'>
                    Showing {filtered.length === 0 ? 0 : indexoffirstuser + 1} to {Math.min(indexoflastuser, filtered.length)} of {filtered.length} records
                </p>
                <div className="flex items-center mt-1 space-x-2">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setcurrentPage(p => p - 1)}
                        className="px-3 py-1 text-[11px] bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 transition"
                    >Prev</button>
                    <p className='text-[11px] text-[#3f7fc8]'>{currentPage} page of {totalpages || 1} pages</p>
                    <button
                        disabled={currentPage >= totalpages}
                        onClick={() => setcurrentPage(p => p + 1)}
                        className="px-3 py-1 text-[11px] bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 transition"
                    >Next</button>
                </div>
            </div>
            {/* Campaign Report Modal */}
{modalCampaign && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-white dark:bg-[#1a1f24] rounded-xl shadow-2xl w-[95vw] max-w-4xl max-h-[85vh] flex flex-col">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#dee2e6] dark:border-[#2a2f35]">
                <div>
                    <p className="text-[13px] font-bold text-[#37715d]">{modalCampaign.campaign_name || 'Campaign Report'}</p>
                    <p className="text-[10px] text-gray-400">ID: {modalCampaign.id} &nbsp;|&nbsp; {modalCampaign.template_name} &nbsp;|&nbsp; <span className="capitalize">{modalCampaign.template_type}</span></p>
                </div>
                <button
                    onClick={() => setModalCampaign(null)}
                    className="text-gray-400 hover:text-red-500 text-[18px] font-bold leading-none transition-colors"
                >
                    &times;
                </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-auto flex-1 p-3">
                {modalLoading ? (
                    <div className="flex items-center justify-center py-12 gap-2 text-[12px] text-gray-400">
                        <div className="w-4 h-4 border-2 border-[#37715d] border-t-transparent rounded-full animate-spin" />
                        Loading report...
                    </div>
                ) : modalData.length === 0 ? (
                    <div className="text-center py-12 text-[12px] text-gray-400">No messages found for this campaign.</div>
                ) : (
                    <table className="w-full border border-[#dee2e6] text-[11px]">
                        <thead>
                            <tr className="bg-[#f6f9fc] dark:bg-[#121519] text-left">
                                {['#', 'Phone Number', 'Status', 'Variables', 'Error'].map(col => (
                                    <th key={col} className="p-2 border border-[#dee2e6] text-[#576071] dark:text-white font-semibold">{col}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {modalData.map((msg, i) => {
                                let vars = '-';
                                try {
                                    const parsed = JSON.parse(msg.variables || '[]');
                                    vars = parsed.length ? parsed.join(', ') : '-';
                                } catch {}
                                return (
                                    <tr key={msg.id || i} className="odd:bg-white even:bg-[#edf1f5] dark:odd:bg-[#121519] dark:even:bg-[#1a1f24]">
                                        <td className="p-1.5 border border-[#dee2e6] text-gray-400">{indexoffirstuser + i + 1}</td>
                                        <td className="p-1.5 border border-[#dee2e6] dark:text-white font-mono">{msg.phone_number || '-'}</td>
                                        <td className="p-1.5 border border-[#dee2e6]">
                                            <StatusBadge status={msg.status} />
                                        </td>
                                        <td className="p-1.5 border border-[#dee2e6] dark:text-white text-gray-400 max-w-[160px] truncate" title={vars}>{vars}</td>
                                        <td className="p-1.5 border border-[#dee2e6]">
                                            {msg.status === 'failed'
                                                ? <span className="text-red-500">{msg.error_message || 'Send failed'}</span>
                                                : <span className="text-gray-300">-</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-2 border-t border-[#dee2e6] dark:border-[#2a2f35] flex justify-between items-center">
                <p className="text-[11px] text-gray-400">Total: <span className="font-bold text-[#37715d]">{modalData.length}</span> messages</p>
                <button
                    onClick={() => setModalCampaign(null)}
                    className="text-[11px] border border-gray-300 px-3 py-1 rounded hover:bg-gray-100 dark:hover:bg-[#2a2f35] transition-colors dark:text-white"
                >
                    Close
                </button>
            </div>
        </div>
    </div>
)}
        </>
    );
}

export default ScheduleReport;